import { useEffect, useState, Children, cloneElement, isValidElement } from "react";
import "../../style/scss/form.scss";
import Spinner from "../Controls/Spinner";
import { useModalBusy } from "./modalBusy";
import Helpers from "../Controls/helpers";
import FormInput from "../Controls/FormInput";
import FormActions from "../Controls/FormActions";
import FormSelect from "../Controls/FormSelect";
import FormAutoComplete from "../Controls/FormAutoComplete";
import FormDate from "../Controls/FormDate";
import FormTextArea from "../Controls/FormTextArea";
import FormCheckbox from "../Controls/FormCheckbox";
import FormSelectList from "../Controls/FormSelectList";
import { IoCloseSharp } from "react-icons/io5";
import { FaRegWindowClose } from "react-icons/fa";
import { CgCloseR } from "react-icons/cg";

// Identity set — do not rely on function.name; production minify mangles it
// (e.g. FormInput → "I"), which breaks formObject injection and leaves parent state empty.
const FORM_CHILD_TYPES = new Set([
  FormInput,
  FormActions,
  FormSelect,
  FormAutoComplete,
  FormDate,
  FormTextArea,
  FormCheckbox,
  FormSelectList,
]);

function isFormChild(child) {
  if (!isValidElement(child) || !child.type) return false;
  if (FORM_CHILD_TYPES.has(child.type)) return true;
  const name = child.type.displayName || child.type.name || "";
  return typeof name === "string" && name.startsWith("Form");
}

function injectFormObject(children, formObj, validationIssues) {
  return Children.map(children, (child) => {
    if (!isValidElement(child)) return child;

    const nestedChildren =
      child.props.children != null
        ? injectFormObject(child.props.children, formObj, validationIssues)
        : child.props.children;

    if (isFormChild(child)) {
      const elementProps = { formObject: formObj };
      const issue = validationIssues.find((i) => {
        return i.field === (child.props.field || child.props.label);
      });
      if (issue) {
        elementProps.valid = "false";
        elementProps.validationmessage = issue.message;
      }
      return cloneElement(child, elementProps, nestedChildren);
    }

    if (nestedChildren !== child.props.children) {
      return cloneElement(child, undefined, nestedChildren);
    }
    return child;
  });
}

function Form(props) {
  const { busy: modalBusy, withBusy } = useModalBusy();
  const [processing, SetProcessing] = useState(true);
  const [validationIssues, updateValidationIssues] = useState([]);
  const [processedChildren, setProcessedChildren] = useState([]);
  const [closeHover, setCloseHover] = useState(false);

  useEffect(() => {
    SetProcessing(false);
  }, []);

  useEffect(() => {
    if (!props.hasOwnProperty("object") || !props.object) {
      console.error("Form requires object property");
    }

    if (props.object && !props.onUpdate) {
      console.error("Form has an object but no onUpdate, thus the form data cannot be used");
    }

    const myChange = (value, field, elementType, e) => {
      if (value && typeof value === "object")
        console.error(
          props.label + " FormSelectList onClick returned object for the value parameter. This must be a value."
        );
      if (field && typeof field === "object")
        console.error(
          props.label + " FormSelectList onClick returned object for the value parameter. This must be a value."
        );
      if (props.onChange) props.onChange(value, field, elementType, e);
      else {
        if (!props.hasOwnProperty("object") && !props.hasOwnProperty("onUpdate")) {
          console.error(
            "Form does not have an object, onChange, or onUpdate property. One of these is required for proper operation of the form"
          );
          debugger;
        }
        if (props.onUpdate) {
          if (Array.isArray(props.object[field])) {
            props.onUpdate({ ...props.object, field: Helpers.toggleValues(props.object[field], value) });
          } else {
            props.onUpdate({ ...props.object, [field]: value });
          }
        }
      }
    };

    const doValidation = () => {
      return new Promise((resolve, reject) => {
        try {
          var issues = [];
          props.children.forEach((child) => {
            validateField(child, issues);
          });
          updateValidationIssues(issues);
          resolve(issues.length === 0);
        } catch (ex) {
          reject(ex);
        }
      });
    };
    const myBlur = (value, field, elementType, e) => {
      validateField(field);
    };


    let formObj = {
      object: props.object,
      onChange: myChange,
      doValidation: doValidation,
      onBlur: myBlur,
      processing: processing,
      setProcessing: SetProcessing,
      formProps: props,
    };

    setProcessedChildren(injectFormObject(props.children, formObj, validationIssues));
  }, [props.children, props.object, props.onUpdate, validationIssues, processing]);

  if (!props.children || props.children.length === 0 || !processedChildren || processedChildren.length === 0)
    return null;

  const validateField = (field, issues) => {
    if (processing) return;
    if (typeof field === "string") {
      field = props.children.find(
        (child) =>
          (child.props.field && child.props.field === field) || (child.props.label && child.props.label === field)
      );
    }
    var fieldName = field.props.field || field.props.label || "";
    var update = !issues;
    if (!issues) issues = validationIssues;
    if (issues && issues.length > 0) {
      var index = issues.indexOf((i) => {
        return i.field === fieldName;
      });
      if (index >= 0) {
        issues.splice(index, 1);
      }
    }
    if (field.props.required) {
      if (!props.object[fieldName] || props.object[fieldName].length === 0) {
        issues.push({ field: fieldName, message: field.props.invalidMessage || "Required" });
      }
    }
    if (field.props.isValid && typeof field.props.isValid === "function" && field.props.invalidMessage) {
      if (!field.props.isValid()) {
        issues.push({ field: fieldName, message: field.props.invalidMessage });
      }
    }
    if (update) updateValidationIssues(issues);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const fromForm =
      props.onSubmitAsync || props.onSaveAsync || props.onSubmit || props.onSave;
    let handler = fromForm;
    if (!handler) {
      const actionsChild = Children.toArray(props.children).find(
        (child) => child && (child.type === FormActions || child.type?.name === "FormActions")
      );
      handler =
        actionsChild?.props?.onSubmitAsync ||
        actionsChild?.props?.onSaveAsync ||
        actionsChild?.props?.Submit;
    }
    if (!handler) return;

    void withBusy(async () => {
      SetProcessing(true);
      try {
        await handler(e);
      } finally {
        SetProcessing(false);
      }
    }).catch((err) => console.error(err));
  };

  var caption = props.label || props.caption || props.header;
  return (
    <>
      <form className={props.className || ""} onSubmit={handleFormSubmit}>
        <fieldset>
          {caption ? <legend>{caption}
            {props.onCancel && (
              closeHover ?
                <CgCloseR
                  className="formCancel"
                  onClick={props.onCancel}
                  onMouseLeave={() => setCloseHover(false)}
                />
                :
                <IoCloseSharp
                  className="formCancel"
                  onClick={props.onCancel}
                  onMouseEnter={() => setCloseHover(true)}
                />
            )}
          </legend> : ""}
          <div className="formBody">
            {processedChildren}
          </div>
        </fieldset>
      </form>
      <Spinner show={(processing || props.processing) && !modalBusy} />
    </>
  );
}

export {
  Form,
  FormInput,
  FormActions,
  FormSelect,
  FormAutoComplete,
  FormDate,
  FormTextArea,
  FormCheckbox,
  FormSelectList,
};